import { useCallback, useRef } from "react";

/**
 * Animate an element's height whenever its CONTENT changes size, instead of letting it
 * snap to the new height.
 *
 * Built for the shared `Modal`, whose height is content-driven: a wizard advancing a step,
 * a validation Alert appearing, a skeleton being replaced by loaded rows, a `Collapse`
 * revealing a block. All of those resized the modal instantly, and because the modal is
 * centred with `translate(-50%, -50%)`, a height jump moves the TOP edge too — so the
 * whole dialog hops under the cursor mid-interaction.
 *
 * ## Why a CALLBACK REF and not `useRef` + `useEffect`
 *
 * This is the whole reason the first version of this hook did nothing, so it is worth
 * being explicit. It used `useRef` plus `useEffect(..., [enabled])`. That effect runs when
 * the component calling the hook mounts — and `Modal` calls it at the top of its body,
 * while rendering `<MuiModal open={false}>`, which renders NO CHILDREN when closed. So
 * `ref.current` was `null`, the effect bailed on its first line, and `[enabled]` never
 * changed, so it never ran again when the modal opened. The observer was never created.
 *
 * Nothing about that failure was visible: the module loaded, the ref would have attached
 * fine, and every guard below was correct. It simply never got a node. A callback ref
 * fires exactly when the node attaches and detaches, which is precisely the
 * mounts-later-than-its-owner case a portal/transition creates.
 *
 * It also means teardown is tied to the NODE rather than to the component, so the
 * observer is disconnected when the modal closes rather than lingering until `Modal`
 * itself unmounts.
 *
 * ## Why a script animation and not `transition: height`
 *
 * The modal's height is `auto`. CSS cannot interpolate to or from `auto`, so a
 * `transition: height` on it is silently inert — it parses, it applies, and it never
 * animates once. The only way to animate a content-driven height is to measure it.
 *
 * The measurement is a Web Animations API call rather than a stateful `height` in React.
 * WAAPI overrides the computed height only for the animation's duration and then releases
 * it, so the element goes straight back to `auto` and keeps sizing itself from content.
 * Holding an explicit pixel height in state would mean owning it forever, and every future
 * content change would have to remember to re-measure or the modal would be stuck at a
 * stale size.
 *
 * ## What it deliberately does not fight
 *
 * `max-height` still clamps, because `max-height` constrains the used height regardless of
 * `height`. Once a modal's content exceeds `calc(100vh - 64px)` the outer height stops
 * changing and the body scrolls instead — the observer sees no size change and correctly
 * does nothing. Recentring is free: the browser recomputes `translate(-50%, -50%)`
 * continuously as the height interpolates.
 *
 * `prefers-reduced-motion` disables it outright. A dialog resizing under you is precisely
 * the vestibular trigger that setting exists for, and snapping is the correct fallback.
 */

const DURATION_MS = 260;

/** Decelerating, no overshoot. A dialog settling into a new size should not bounce —
 *  overshoot on a container reads as instability rather than polish. */
const EASING = "cubic-bezier(0.32, 0.72, 0, 1)";

/** Sub-2px changes are sub-pixel rounding and font-metric noise, not content changes.
 *  Animating them would fire constantly and read as jitter. */
const MIN_DELTA_PX = 2;

export const useAnimatedHeight = <T extends HTMLElement>(enabled = true) => {
  // Holds the teardown for the CURRENTLY attached node, so a re-attach (modal reopened)
  // cannot leave the previous observer running.
  const teardownRef = useRef<(() => void) | null>(null);

  return useCallback(
    (el: T | null) => {
      teardownRef.current?.();
      teardownRef.current = null;

      if (!el || !enabled) return;
      if (typeof ResizeObserver === "undefined") return;
      if (typeof el.animate !== "function") return;
      if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;

      // null until the first observation. The initial measurement is the modal's opening
      // size and must NOT animate — it arrives with the Fade, and animating height on top
      // of the entrance reads as a wobble.
      let prev: number | null = null;
      let animating = false;
      let animation: Animation | null = null;

      const run = (from: number, to: number) => {
        animating = true;
        // While the box is shorter than its content the body would briefly show a
        // scrollbar and then drop it. Hiding overflow for the duration keeps the growth
        // clean; the body's own `overflow: auto` is untouched and takes over after.
        const restoreOverflow = el.style.overflow;
        el.style.overflow = "hidden";

        animation = el.animate(
          [{ height: `${from}px` }, { height: `${to}px` }],
          { duration: DURATION_MS, easing: EASING },
        );

        const settle = () => {
          animating = false;
          animation = null;
          el.style.overflow = restoreOverflow;
          // Content can change again mid-animation (a step advancing twice, an async
          // result landing). The run we just finished targeted a now-stale height, so
          // re-measure and chain rather than letting WAAPI release into a snap.
          const settled = el.offsetHeight;
          prev = settled;
          if (Math.abs(settled - to) >= MIN_DELTA_PX) run(to, settled);
        };

        animation.onfinish = settle;
        animation.oncancel = () => {
          animating = false;
          animation = null;
          el.style.overflow = restoreOverflow;
          prev = el.offsetHeight;
        };
      };

      const observer = new ResizeObserver(() => {
        // Our own animation resizes the element on every frame, which re-enters this
        // callback. Without the guard the observer would chase its own tail.
        if (animating) return;
        const next = el.offsetHeight;
        const from = prev;
        prev = next;
        if (from === null || Math.abs(next - from) < MIN_DELTA_PX) return;
        // A hidden document does not run the rendering steps, so ResizeObserver is not
        // delivered while the tab is backgrounded — every size change that happened away
        // from the user arrives in one batch on return. Animating then would replay a
        // transition for content they never watched change. Adopt the new height silently
        // instead; `prev` is already updated above.
        if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
        run(from, next);
      });

      observer.observe(el);
      teardownRef.current = () => {
        observer.disconnect();
        animation?.cancel();
      };
    },
    [enabled],
  );
};
